import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';

const SolutionSection = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="bg-[#111111] py-20 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header with Label and Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="text-[#C8102E] text-base md:text-lg tracking-[0.2em] font-medium uppercase mb-6">
            {data.label}
          </div>
          
          {data.headline && (
            <h2
              className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight whitespace-pre-line"
              style={{ fontFamily: "'Ubuntu', sans-serif" }}
            >
              {data.headline}
            </h2>
          )}
        </motion.div>

        {/* Benefits Grid - Problem Section Style (Now FIRST) */}
        {data.benefits && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10"
          >
            {data.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.2 }
                }}
                className="group bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl p-6 hover:border-[#C8102E]/50 transition-all duration-300"
              >
                {/* Benefit Title with Icon */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#C8102E]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-[#C8102E]" />
                  </div>
                  <h3 
                    className="text-white font-bold text-lg leading-tight"
                    style={{ fontFamily: "'Ubuntu', sans-serif" }}
                  >
                    {benefit.title}
                  </h3>
                </div>
                
                {/* Description */}
                <p 
                  className="text-[#F0EBE3]/70 text-sm leading-relaxed pl-11"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                >
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Section Title: İki Lezzet Dünyası, Tek Marka */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center py-10 mb-8"
        >
          <h3
            className="text-white font-bold text-4xl md:text-5xl lg:text-6xl inline-block relative"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            İki Lezzet Dünyası, Tek Marka
            <motion.span 
              className="absolute -bottom-3 left-0 right-0 h-1 bg-[#C8102E]"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
            />
          </h3>
        </motion.div>

        {/* Main Feature Cards - EXPRESS & CHEF'S TABLE (Now AFTER benefits) */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {data.features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ 
                duration: 0.7, 
                delay: 1.1 + index * 0.2,
                ease: [0.25, 0.4, 0.25, 1]
              }}
              whileHover={{ 
                y: -8, 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              className="bg-[#1A1A1A] rounded-xl overflow-hidden group cursor-pointer border-2 border-[#C8102E]/30 hover:border-[#C8102E] transition-all duration-300 shadow-lg shadow-[#C8102E]/5"
            >
              <motion.div 
                className="h-1.5 bg-[#C8102E]"
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.8, delay: 1.3 + index * 0.2 }}
              ></motion.div>
              <div className="p-10">
                <div className="flex items-start justify-between mb-6">
                  <motion.div 
                    className="text-[#C8102E] text-base font-mono tracking-wider"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : {}}
                    transition={{ delay: 1.4 + index * 0.2 }}
                  >
                    {feature.number}
                  </motion.div>
                  <motion.div 
                    className="bg-[#C8102E] text-white px-5 py-1.5 rounded-full text-sm font-medium"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      delay: 1.5 + index * 0.2
                    }}
                  >
                    {feature.badge}
                  </motion.div>
                </div>

                <motion.h3
                  className="text-white text-4xl font-bold mb-5 tracking-wide"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 1.6 + index * 0.2 }}
                >
                  {feature.title}
                </motion.h3>

                <motion.p
                  className="text-[#F0EBE3] text-lg leading-relaxed mb-6"
                  style={{ fontFamily: "'Ubuntu', sans-serif" }}
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 1.7 + index * 0.2 }}
                >
                  {feature.description}
                </motion.p>

                <motion.div 
                  className="text-[#C8102E] text-sm font-medium"
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 1.8 + index * 0.2 }}
                >
                  {feature.tags}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.8 }}
          className="text-center"
        >
          <p
            className="text-[#F0EBE3]/80 text-lg italic"
            style={{ fontFamily: "'Ubuntu', sans-serif" }}
          >
            {data.connector}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;
